// src/pages/CommunityDetails.js

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    FiPlus, FiTrash2, FiUser, FiPhone, FiMail, FiEdit2, FiArrowLeft, 
    FiSave, FiXCircle, FiKey, FiCopy, FiEye, FiEyeOff, FiFileText, 
    FiUploadCloud, FiDownload, FiImage, FiX, FiExternalLink, FiFilm,
    FiChevronRight, FiChevronDown, FiShare2, FiMoreVertical, FiHome,
    FiMap, FiCheckCircle 
} from 'react-icons/fi';
import { FaApple, FaGoogle } from 'react-icons/fa';
import MapView from '../components/MapView';
import { useApi } from '../hooks/useApi';
import Accordion from '../components/Accordion';
import { AuthContext } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import CustomFileUpload from '../components/CustomFileUpload';
import TicketQueue from '../components/TicketQueue'; 
import RadialStatusMenu from '../components/RadialStatusMenu';
import useLongPress from '../hooks/useLongPress';

// --- HELPERS & SUB-COMPONENTS ---

const ClipboardNotification = ({ message, isVisible }) => {
    if (!isVisible) return null;
    return ( <div className={`clipboard-notification ${isVisible ? 'visible' : ''}`}>{message}</div> );
};

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) { return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`; }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const formatFileSize = (bytes) => { 
    if (bytes === 0) return '0 Bytes'; 
    const k = 1024; 
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k)); 
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// --- MOBILE CARD COMPONENTS ---

const MobileContactCard = ({ contact, onCopy, onEdit, onDelete, canModify, index = 0 }) => {
    return (
        <div 
            className="mobile-generic-card"
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
            }}
        >
            <div className="mobile-card-header">
                <div className="mobile-card-main-content" style={{width: '100%'}}>
                    <div className="mobile-card-icon-container">
                        <FiUser />
                    </div>
                    <div className="mobile-card-text">
                        <h4 className="mobile-card-title">{contact.name}</h4>
                        <p className="mobile-card-subtitle">{contact.title || 'No Title'}</p>
                    </div>
                </div>
            </div>

            {(contact.phone || contact.email) && (
                <div className="mobile-card-details">
                    {contact.phone && (
                        <div className="mobile-detail-row" onClick={() => onCopy(contact.phone, 'Phone')}>
                            <FiPhone size={14} className="text-muted"/>
                            <span>{formatPhoneNumber(contact.phone)}</span>
                        </div>
                    )}
                    {contact.email && (
                        <div className="mobile-detail-row" onClick={() => onCopy(contact.email, 'Email')}>
                            <FiMail size={14} className="text-muted"/>
                            <span>{contact.email}</span>
                        </div>
                    )}
                </div>
            )}

            {canModify && (
                <div className="mobile-card-actions">
                    <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); onEdit(contact); }}>
                        <FiEdit2 className="btn-icon"/> Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(contact._id); }}>
                        <FiTrash2 className="btn-icon"/> Delete
                    </button>
                </div>
            )}
        </div>
    );
};

const MobileCredentialCard = ({ credential, onCopy, onEdit, onDelete, canModify, index = 0 }) => {
    const [showPass, setShowPass] = useState(false);

    return (
        <div 
            className="mobile-generic-card"
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
            }}
        >
            <div className="mobile-card-header">
                <div className="mobile-card-main-content">
                    <div className="mobile-card-icon-container" style={{backgroundColor: 'var(--blue-50)', color: 'var(--blue-600)'}}>
                        <FiKey />
                    </div>
                    <div className="mobile-card-text">
                        <h4 className="mobile-card-title">{credential.service}</h4>
                        <p className="mobile-card-subtitle">{credential.username || 'No Username'}</p>
                    </div>
                </div>
            </div>

            <div className="mobile-card-details">
                <div className="mobile-detail-row" style={{justifyContent: 'space-between'}}>
                    <span style={{fontFamily: 'monospace', fontSize: '1rem'}}>
                        {showPass ? credential.password : '••••••••'}
                    </span>
                    <div style={{display:'flex', gap:'0.5rem'}}>
                        <button className="btn-icon-only" onClick={() => setShowPass(!showPass)}>
                            {showPass ? <FiEyeOff/> : <FiEye/>}
                        </button>
                        <button className="btn-icon-only" onClick={() => onCopy(credential.password, 'Password')}>
                            <FiCopy/>
                        </button>
                    </div>
                </div>
                {credential.notes && (
                    <div className="mobile-detail-row" style={{borderTop:'1px solid var(--gray-200)', paddingTop:'0.5rem', marginTop:'0.25rem', fontSize:'0.8rem', fontStyle:'italic'}}>
                        {credential.notes}
                    </div>
                )}
            </div>

            <div className="mobile-card-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => {
                      const link = `${window.location.origin}/customers/${credential.communityId}?credentialId=${credential._id}`;
                      onCopy(link, 'Link');
                }}>
                    <FiShare2/>
                </button>
                {canModify && (
                    <>
                        <button className="btn btn-secondary btn-sm" onClick={() => onEdit(credential)}>
                            <FiEdit2/>
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDelete(credential._id)}>
                            <FiTrash2/>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

const MobileDocumentCard = ({ doc, onView, onDownload, onDelete, sourceTicket, isImg, isVid, index = 0 }) => {
    let IconComponent = FiFileText;
    if (isImg) IconComponent = FiImage;
    else if (isVid) IconComponent = FiFilm;

    return (
        <div 
            className="mobile-generic-card"
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
             }}
        >
            <div className="mobile-card-header">
                <div className="mobile-card-main-content" style={{ minWidth: 0 }}>
                    <div className="list-view-image-thumbnail-wrapper" style={{width:'50px', height:'50px', borderRadius:'8px', backgroundColor: 'var(--gray-100)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0}}>
                        {(isImg || isVid) ? (
                            isImg ? (
                                <img 
                                    src={doc.filePath} 
                                    alt={doc.originalName} 
                                    className="list-view-image-thumbnail"
                                    onClick={() => onView(doc)}
                                    style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px' }}
                                />
                            ) : (
                                <video 
                                    src={doc.filePath} 
                                    className="list-view-image-thumbnail"
                                    muted
                                    preload="metadata"
                                    onClick={() => onView(doc)}
                                    style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px' }}
                                />
                            )
                        ) : (
                             <IconComponent size={24} style={{ color: 'var(--gray-500)' }} />
                        )}
                    </div>
                    
                    <div className="mobile-card-text" style={{ minWidth: 0, paddingRight: '0.5rem' }}>
                        <h4 className="mobile-card-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
                             {doc.originalName}
                        </h4>
                        <p className="mobile-card-subtitle">{formatFileSize(doc.fileSize)} • {new Date(doc.createdAt).toLocaleDateString()}</p>
                    </div>
                </div>
            </div>

            {sourceTicket && (
                <div className="mobile-card-details" style={{marginTop: '0.5rem', backgroundColor: 'var(--blue-50)', color: 'var(--blue-800)'}}>
                    <div className="mobile-detail-row">
                        <FiArrowLeft size={12} style={{ transform: 'rotate(180deg)' }}/> 
                        <span>Ticket: {sourceTicket.subject} (#{sourceTicket._id.substring(0,6)})</span>
                    </div>
                </div>
            )}

            <div className="mobile-card-actions" style={{ display: 'flex', gap: '0.5rem', width: '100%', marginTop: '0.75rem', borderTop: '1px solid var(--gray-100)', paddingTop: '0.75rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => onView(doc)} style={{ flex: 1, justifyContent: 'center' }}>
                    <FiEye className="btn-icon"/> View
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onDownload(doc)} style={{ flex: 1, justifyContent: 'center' }}>
                    <FiDownload className="btn-icon"/> Get
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => onDelete(doc._id)} style={{ flex: 1, justifyContent: 'center' }}>
                    <FiTrash2 className="btn-icon"/> Del
                </button>
            </div>
        </div>
    );
};

// --- MODALS ---

const AddContactModal = ({ isOpen, onClose, onAddContact, communityId }) => {
    const [name, setName] = useState(''); const [phone, setPhone] = useState(''); const [email, setEmail] = useState(''); const [title, setTitle] = useState(''); const [notes, setNotes] = useState(''); const overlayMousedownTarget = useRef(null);
    if (!isOpen) return null;
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    const handlePhoneChange = (e) => { setPhone(formatPhoneNumber(e.target.value)); };
    const handleSubmit = (e) => { e.preventDefault(); if (!name.trim()) { alert('Contact name is required.'); return; } const rawPhone = phone.replace(/\D/g, ''); if (!rawPhone && !email.trim()) { alert('Either a phone number or an email must be provided.'); return; } onAddContact({ name, phone: rawPhone, email, title, notes, communityId }); setName(''); setPhone(''); setEmail(''); setTitle(''); setNotes(''); onClose(); };
    return ( <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}><div className="modal-content"><div className="modal-header"><h3 className="modal-title">Add New Contact</h3><button onClick={onClose} className="modal-close-btn">×</button></div><form onSubmit={handleSubmit} className="modal-form"><div className="modal-body"><div className="form-group"><label htmlFor="name">Name</label><input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required /></div><div className="form-group"><label htmlFor="phone">Phone Number</label><input type="tel" id="phone" value={phone} onChange={handlePhoneChange} placeholder="(555) 555-5555" /></div><div className="form-group"><label htmlFor="email">Email</label><input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div><div className="form-group"><label htmlFor="title">Title</label><input type="text" id="title" value={title} onChange={(e) => setTitle(e.target.value)} /></div><div className="form-group"><label htmlFor="notes">Notes</label><textarea id="notes" name="notes" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)}></textarea></div></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Save Contact</button></div></form></div></div> );
};

const EditContactModal = ({ isOpen, onClose, onUpdateContact, contact }) => {
    const [formData, setFormData] = useState({ name: '', phone: '', email: '', title: '', notes: '' }); const overlayMousedownTarget = useRef(null);
    useEffect(() => { if (isOpen && contact) { setFormData({ name: contact.name || '', phone: formatPhoneNumber(contact.phone || ''), email: contact.email || '', title: contact.title || '', notes: contact.notes || '' }); } }, [isOpen, contact]);
    if (!isOpen || !contact) return null;
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    const handleChange = (e) => { const { name, value } = e.target; if (name === 'phone') { setFormData(prev => ({ ...prev, phone: formatPhoneNumber(value) })); } else { setFormData(prev => ({ ...prev, [name]: value })); } };
    const handleSubmit = (e) => { e.preventDefault(); if (!formData.name.trim()) { alert('Contact name is required.'); return; } const rawPhone = formData.phone.replace(/\D/g, ''); if (!rawPhone && !formData.email.trim()) { alert('Either a phone number or an email must be provided.'); return; } onUpdateContact(contact._id, { ...formData, phone: rawPhone }); onClose(); };
    return ( <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}><div className="modal-content"><div className="modal-header"><h3 className="modal-title">Edit Contact</h3><button onClick={onClose} className="modal-close-btn">×</button></div><form onSubmit={handleSubmit} className="modal-form"><div className="modal-body"><div className="form-group"><label htmlFor="edit-name">Name</label><input type="text" id="edit-name" name="name" value={formData.name} onChange={handleChange} required /></div><div className="form-group"><label htmlFor="edit-phone">Phone Number</label><input type="tel" id="edit-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 555-5555" /></div><div className="form-group"><label htmlFor="edit-email">Email</label><input type="email" id="edit-email" name="email" value={formData.email} onChange={handleChange} /></div><div className="form-group"><label htmlFor="edit-title">Title</label><input type="text" id="edit-title" name="title" value={formData.title} onChange={handleChange} /></div><div className="form-group"><label htmlFor="edit-notes">Notes</label><textarea id="edit-notes" name="notes" rows="3" value={formData.notes} onChange={handleChange}></textarea></div></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Save Changes</button></div></form></div></div> );
};

const AddEditCredentialModal = ({ isOpen, onClose, onSubmit, communityId, credentialToEdit, isReadOnly = false }) => {
    const isEditMode = !!credentialToEdit; 
    const initialFormState = { service: '', username: '', password: '', notes: '', communityId: communityId }; 
    const [formData, setFormData] = useState(initialFormState); 
    const [showPassword, setShowPassword] = useState(false);
    const overlayMousedownTarget = useRef(null);
    useEffect(() => { if (isOpen) { setFormData(isEditMode ? { ...credentialToEdit } : initialFormState); setShowPassword(false); } }, [isOpen, credentialToEdit, communityId, isEditMode]);
    if (!isOpen) return null;
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
    const handleSubmit = (e) => { e.preventDefault(); if (isReadOnly) return; if (!formData.service || !formData.password) { alert('Service and Password fields are required.'); return; } onSubmit(formData); onClose(); };
    const modalTitle = isReadOnly ? 'View Credential' : (isEditMode ? 'Edit Credential' : 'Add New Credential');
    return ( 
        <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <div className="modal-content">
                <div className="modal-header"><h3 className="modal-title">{modalTitle}</h3><button onClick={onClose} className="modal-close-btn">×</button></div>
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="modal-body">
                        <div className="form-group"><label htmlFor="service">Service/Device</label><input type="text" id="service" name="service" value={formData.service} onChange={handleChange} required autoFocus disabled={isReadOnly} /></div>
                        <div className="form-group"><label htmlFor="username">Username</label><div style={{ position: 'relative' }}><input type="text" id="username" name="username" value={formData.username} onChange={handleChange} disabled={isReadOnly} style={{ paddingRight: '2.5rem' }}/>{formData.username && ( <button type="button" className="btn-icon-only" style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => navigator.clipboard.writeText(formData.username)} title="Copy Username"><FiCopy size={14}/></button> )}</div></div>
                        <div className="form-group"><label htmlFor="password">Password</label><div style={{ display: 'flex', gap: '0.5rem' }}><div style={{ position: 'relative', flex: 1 }}><input type={showPassword ? "text" : "password"} id="password" name="password" value={formData.password} onChange={handleChange} required disabled={isReadOnly} style={{ width: '100%', paddingRight: '2.5rem' }}/><button type="button" className="btn-icon-only" style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => setShowPassword(!showPassword)} title={showPassword ? "Hide" : "Show"}>{showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}</button></div>{isReadOnly && ( <button type="button" className="btn btn-secondary" onClick={() => navigator.clipboard.writeText(formData.password)} title="Copy Password"><FiCopy /></button> )}</div></div>
                        <div className="form-group"><label htmlFor="notes">Notes</label><textarea id="notes" name="notes" rows="3" value={formData.notes} onChange={handleChange} disabled={isReadOnly}></textarea></div>
                    </div>
                    <div className="modal-footer">{isReadOnly ? ( <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button> ) : ( <> <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button> <button type="submit" className="btn btn-primary">{isEditMode ? 'Save Changes' : 'Save Credential'}</button> </> )}</div>
                </form>
            </div>
        </div> 
    );
};

const DocumentViewerModal = ({ isOpen, onClose, document, content }) => {
    if (!isOpen || !document) return null;
    return ( <div className="modal-overlay" onClick={onClose}><div className="modal-content modal-content-large" onClick={e => e.stopPropagation()}><div className="modal-header"><h3>{document.originalName}</h3><button onClick={onClose} className="modal-close-btn">×</button></div><div className="modal-body" style={{ background: '#f8f9fa' }}><pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, height: '100%', overflowY: 'auto' }}><code>{content}</code></pre></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Close</button></div></div></div> );
};

const ImageViewerModal = ({ isOpen, onClose, imageUrl, docName }) => {
    useEffect(() => { return () => { if (imageUrl) { URL.revokeObjectURL(imageUrl); } }; }, [imageUrl]);
    if (!isOpen || !imageUrl) return null;
    return ( <div className="modal-overlay" onClick={onClose}><div className="image-viewer-modal-content" onClick={e => e.stopPropagation()}><button onClick={onClose} className="image-viewer-close-btn" title="Close"><FiX size={20} /></button><img src={imageUrl} alt={docName} /></div></div> );
};

const VideoViewerModal = ({ isOpen, onClose, videoUrl, docName }) => {
    useEffect(() => { return () => { if (videoUrl) { URL.revokeObjectURL(videoUrl); } }; }, [videoUrl]);
    if (!isOpen || !videoUrl) return null;
    return ( <div className="modal-overlay" onClick={onClose}><div className="image-viewer-modal-content" onClick={e => e.stopPropagation()}><button onClick={onClose} className="image-viewer-close-btn" title="Close"><FiX size={20} /></button><video controls autoPlay name="media"><source src={videoUrl} />Your browser does not support the video tag.</video></div></div> );
};

// --- LIST ITEMS (DESKTOP) ---

const CredentialListItem = ({ credential, onCopy, onEdit, onDelete }) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const { user } = useContext(AuthContext);
    const canModify = useMemo(() => {
        if (!user || !credential) return false;
        const creatorId = credential.createdBy?._id || credential.createdBy;
        const isOwner = creatorId === user.id;
        const isAdmin = user.roles.includes('Admin') || user.roles.includes('Giga Admin');
        return isOwner || isAdmin;
    }, [user, credential]);
    const createdDate = new Date(credential.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const handleShare = () => { const link = `${window.location.origin}/customers/${credential.communityId}?credentialId=${credential._id}`; onCopy(link, 'Direct link'); };
    return (
        <div className="credential-item">
            <div className="credential-info">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}><h4 className="credential-service-name">{credential.service}</h4><p className="item-meta" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Added by {credential.createdBy?.name || 'System'} on {createdDate}</p></div>
                <div className="credential-details">
                    {credential.username && ( <div className="credential-detail-row"><span className="credential-label">USER</span><span className="credential-value">{credential.username}</span><button className="btn-icon-only" title="Copy Username" onClick={() => onCopy(credential.username, 'Username')}><FiCopy /></button></div> )}
                    <div className="credential-detail-row"><span className="credential-label">PASS</span><span className={`credential-value ${!isPasswordVisible ? 'credential-password-hidden' : ''}`}>{credential.password}</span><button className="btn-icon-only" title="Copy Password" onClick={() => onCopy(credential.password, 'Password')}><FiCopy /></button><button className="btn-icon-only" title={isPasswordVisible ? 'Hide Password' : 'Show Password'} onClick={() => setIsPasswordVisible(!isPasswordVisible)}>{isPasswordVisible ? <FiEyeOff /> : <FiEye />}</button></div>
                    {credential.notes && ( <div className="credential-detail-row"><span className="credential-label">NOTES</span><span className="credential-value" style={{ fontFamily: 'inherit', letterSpacing: 'normal', whiteSpace: 'pre-wrap' }}>{credential.notes}</span></div> )}
                </div>
            </div>
            <div className="credential-actions"><button className="btn btn-secondary btn-sm" onClick={handleShare} title="Copy Direct Link"><FiShare2 className="btn-icon"/> Share</button>{canModify && ( <> <button className="btn btn-secondary btn-sm" onClick={() => onEdit(credential)}><FiEdit2 className="btn-icon"/> Edit</button> <button className="btn btn-danger btn-sm" onClick={() => onDelete(credential._id)}><FiTrash2 className="btn-icon"/> Delete</button> </> )}</div>
        </div>
    );
};

const ContactListItem = ({ contact, onCopy, onEdit, onDelete }) => {
    const { user } = useContext(AuthContext);
    const canModify = useMemo(() => {
        if (!user || !contact) return false;
        const creatorId = contact.createdBy?._id || contact.createdBy;
        const isOwner = creatorId === user.id;
        const isAdmin = user.roles.includes('Admin') || user.roles.includes('Giga Admin');
        return isOwner || isAdmin;
    }, [user, contact]);
    return (
        <div className="contact-list-item clickable-row" onClick={() => canModify && onEdit(contact)} style={!canModify ? {cursor: 'default'} : {}}>
            <div className="contact-icon"><FiUser size={24} /></div>
            <div className="contact-details">
                <p className="contact-name">{contact.name} {contact.title && <span className="contact-title">({contact.title})</span>}</p>
                <div className="contact-info">{contact.phone && ( <button className="contact-info-item" title="Copy phone number" onClick={(e) => { e.stopPropagation(); onCopy(contact.phone, 'Phone'); }}><FiPhone size={12}/> {formatPhoneNumber(contact.phone)}</button> )}{contact.email && ( <button className="contact-info-item" title="Copy email address" onClick={(e) => { e.stopPropagation(); onCopy(contact.email, 'Email'); }}><FiMail size={12}/> {contact.email}</button> )}</div>
            </div>
            {canModify && ( <div className="contact-actions"><button className="btn btn-edit btn-icon-only" onClick={(e) => { e.stopPropagation(); onEdit(contact); }} title="Edit Contact"><FiEdit2 /></button><button className="btn btn-delete btn-icon-only" onClick={(e) => { e.stopPropagation(); onDelete(contact._id); }} title="Delete Contact"><FiTrash2 /></button></div> )}
        </div>
    );
};

// --- CONTENT TAB COMPONENT ---

const CommunityContentTabs = ({ isMobile, community, tickets, contacts, onAddContact, onUpdateContact, onDeleteContact, onUpdateCommunity, credentials, onAddCredential, onUpdateCredential, onDeleteCredential, documents, onAddDocument, onDeleteDocument, navigate, apiCallForBlob }) => {
    const [activeTab, setActiveTab] = useState(isMobile ? 'Community Info' : 'All');
    const TABS = ['Tickets', 'Contacts', 'Credentials', 'Documentation', 'Community Info'];
    
    // UI State
    const [isAddContactModalOpen, setAddContactModalOpen] = useState(false);
    const [isEditContactModalOpen, setEditContactModalOpen] = useState(false);
    const [contactToEdit, setContactToEdit] = useState(null);
    const [isCredentialModalOpen, setIsCredentialModalOpen] = useState(false);
    const [credentialToEdit, setCredentialToEdit] = useState(null);
    const [isCredentialReadOnly, setIsCredentialReadOnly] = useState(false); 
    const [notification, setNotification] = useState({ isVisible: false, message: '' });
    const [editingField, setEditingField] = useState(null);
    const [editedData, setEditedData] = useState({ ...community });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [expandedTickets, setExpandedTickets] = useState(new Set());
    const fileUploadRef = useRef(null); 
    
    // ✅ RADIAL MENU STATE
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [statusMenuPosition, setStatusMenuPosition] = useState({ x: 0, y: 0 });

    const { user } = useContext(AuthContext);
    const location = useLocation();
    
    // Viewer State
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
    const [viewingDocument, setViewingDocument] = useState(null);
    const [viewerContent, setViewerContent] = useState('');
    const [pythonFileContents, setPythonFileContents] = useState({});

    const canEdit = useMemo(() => user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), [user]);
    
    const communityDocuments = useMemo(() => {
        if (!documents || !community?._id) return [];
        const docMap = new Map();
        const targetCommunityId = String(community._id);
        const validTicketIds = new Set(tickets.map(t => String(t._id)));
        documents.forEach(doc => {
            const docOwnerId = String(doc.ownerId);
            const associatedId = doc.associatedCommunityId ? String(doc.associatedCommunityId) : null;
            const isDirectCommunityDoc = (doc.ownerType === 'Community' && docOwnerId === targetCommunityId);
            const isLinkedViaDb = (associatedId === targetCommunityId);
            const isOwnedByCommunityTicket = (doc.ownerType === 'Ticket' && validTicketIds.has(docOwnerId));
            if (isDirectCommunityDoc || isLinkedViaDb || isOwnedByCommunityTicket) { 
                if (!docMap.has(doc._id)) { docMap.set(doc._id, doc); } 
            } 
        });
        return Array.from(docMap.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [documents, community, tickets]);

    const communityContacts = contacts.filter(c => c.communityId === community._id);
    const communityCredentials = credentials.filter(c => c.communityId === community._id);
    
    const communityStatusOptions = useMemo(() => [
        { label: 'Active', icon: FiCheckCircle },
        { label: 'Inactive', icon: FiXCircle }
    ], []);

    useEffect(() => {
        setEditedData({ ...community });
        if(activeTab !== 'Community Info') {
            setEditingField(null);
        }
    }, [community, activeTab]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const credentialId = searchParams.get('credentialId');
        if (credentialId && credentials.length > 0) {
            const target = credentials.find(c => c._id === credentialId);
            if (target) {
                setActiveTab('Credentials');
                setCredentialToEdit(target);
                setIsCredentialReadOnly(true);
                setIsCredentialModalOpen(true);
            }
        }
    }, [location.search, credentials]);

    const handleCopy = (textToCopy, type) => {
        if (!textToCopy) return;
        navigator.clipboard.writeText(textToCopy).then(() => {
            setNotification({ isVisible: true, message: `${type} copied to clipboard!` });
        }).catch(err => {
            console.error('Failed to copy: ', err);
            setNotification({ isVisible: true, message: 'Failed to copy.' });
        }).finally(() => {
            setTimeout(() => {
                setNotification({ isVisible: false, message: '' });
            }, 2000);
        });
    };
    
    // ✅ RADIAL MENU HANDLERS (TAP & LONG PRESS)
    const handleStatusClick = (e) => {
        if (!canEdit) return;
        e.preventDefault();
        setStatusMenuPosition({ x: e.clientX || window.innerWidth / 2, y: e.clientY || window.innerHeight / 2 });
        setIsStatusMenuOpen(true);
    };

    const statusLongPress = useLongPress((e) => {
        if (!canEdit) return;
        const clientX = e.touches ? e.touches[0].clientX : (e.clientX || window.innerWidth / 2);
        const clientY = e.touches ? e.touches[0].clientY : (e.clientY || window.innerHeight / 2);
        
        setStatusMenuPosition({ x: clientX, y: clientY });
        setIsStatusMenuOpen(true);
    }, { delay: 400, shouldPreventDefault: false });

    const handleStatusSelect = (newStatus) => {
        if (newStatus !== community.status) {
            onUpdateCommunity(community._id, { status: newStatus });
        }
        setIsStatusMenuOpen(false);
    };

    const handleDoubleClick = (fieldName) => { if (canEdit) { setEditingField(fieldName); } };
    const handleInfoChange = (e) => { const { name, value } = e.target; if (['street', 'city', 'state', 'zip'].includes(name)) { setEditedData(prev => ({ ...prev, address: { ...(prev.address || {}), [name]: value } })); } else { setEditedData(prev => ({ ...prev, [name]: value })); } };
    const handleSaveInline = (fieldName) => { if (!canEdit || !editedData) return; let updatePayload;
if (['street', 'city', 'state', 'zip'].includes(fieldName)) { updatePayload = { address: editedData.address }; } else { updatePayload = { [fieldName]: editedData[fieldName] }; } onUpdateCommunity(community._id, updatePayload); setEditingField(null); };
    const handleKeyDown = (e, fieldName) => { if (e.key === 'Enter') { handleSaveInline(fieldName); } else if (e.key === 'Escape') { setEditedData({ ...community }); setEditingField(null); } };
    
    const handleOpenEditContactModal = (contact) => { setContactToEdit(contact); setEditContactModalOpen(true); };
    const handleCloseEditContactModal = () => { setContactToEdit(null); setEditContactModalOpen(false); };
    const handleOpenAddCredentialModal = () => { setCredentialToEdit(null); setIsCredentialReadOnly(false); setIsCredentialModalOpen(true); };
    const handleOpenEditCredentialModal = (cred) => { setCredentialToEdit(cred); setIsCredentialReadOnly(false); setIsCredentialModalOpen(true); };
    const handleCloseCredentialModal = () => { setCredentialToEdit(null); setIsCredentialModalOpen(false); };
    const handleCredentialSubmit = (formData) => { if (credentialToEdit) { onUpdateCredential(credentialToEdit._id, formData); } else { onAddCredential(formData); } };
    const handleFileSelect = (e) => { if (e.target.files && e.target.files[0]) { setSelectedFile(e.target.files[0]); } };
    const handleFileUpload = async () => { if (!selectedFile) { alert('Please select a file to upload.'); return; } setIsUploading(true); const formData = new FormData(); formData.append('file', selectedFile); formData.append('ownerId', community._id); formData.append('ownerType', 'Community'); const result = await onAddDocument(formData); if (result.success) { setSelectedFile(null); if(fileUploadRef.current) fileUploadRef.current.clear(); } setIsUploading(false); };
    const handleDeleteDocument = (docId) => { if (window.confirm('Are you sure you want to permanently delete this document?')) { onDeleteDocument(docId); } };
    const isPythonFile = (fileName) => fileName.toLowerCase().endsWith('.py');
    const isViewableTextFile = (fileType, fileName) => { if (isPythonFile(fileName)) return false; const viewableTypes = [ 'text/plain', 'text/csv', 'text/html', 'text/xml', 'application/json', 'application/javascript' ]; return viewableTypes.some(type => fileType.startsWith(type)); };
    const isViewableImage = (fileType) => fileType.startsWith('image/');
    const isViewableVideo = (fileType) => fileType.startsWith('video/');
    
    const handleView = async (doc) => {
        const blob = await apiCallForBlob(`documents/stream/${doc._id}`);
        if (!blob) { alert('Could not load file for preview.'); return; }
        if (isViewableImage(doc.fileType)) { const url = URL.createObjectURL(blob); setViewerContent(url); setViewingDocument(doc); setIsImageViewerOpen(true); } else if (isViewableVideo(doc.fileType)) { const url = URL.createObjectURL(blob); setViewerContent(url); setViewingDocument(doc); setIsVideoViewerOpen(true); } else if (isViewableTextFile(doc.fileType, doc.originalName)) { const text = await blob.text(); setViewerContent(text); setViewingDocument(doc); setIsViewerOpen(true); }
    };
    
    const handleDownload = async (doc) => {
        const blob = await apiCallForBlob(`documents/stream/${doc._id}`);
        if (blob) { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = doc.originalName; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); } else { alert('Could not download file.'); }
    };

    const handleAccordionToggle = async (doc) => {
        if (pythonFileContents[doc._id]) { return; }
        setPythonFileContents(prev => ({ ...prev, [doc._id]: 'Loading...' }));
        const blob = await apiCallForBlob(`documents/stream/${doc._id}`);
        if (blob) { const textContent = await blob.text(); setPythonFileContents(prev => ({ ...prev, [doc._id]: textContent })); } else { setPythonFileContents(prev => ({ ...prev, [doc._id]: 'Error loading content.' })); }
    };

    const toggleTicketExpansion = (e, ticketId) => {
        e.stopPropagation();
        setExpandedTickets(prev => { const next = new Set(prev); if (next.has(ticketId)) next.delete(ticketId); else next.add(ticketId); return next; });
    };

    const renderContent = () => {
        if (activeTab === 'Community Info') {
            if (isMobile) {
                return (
                    <div className="mobile-generic-card" style={{ animation: 'slideFadeInWithGlow 0.4s ease-out backwards' }}>
                        <div style={{ marginBottom: '1rem', borderBottom: '1px solid var(--gray-100)', paddingBottom: '1rem' }}>
                            <div className="detail-item"><span className="detail-label">NAME</span>{editingField === 'name' ? ( <input type="text" name="name" value={editedData.name} onChange={handleInfoChange} onBlur={() => handleSaveInline('name')} className="form-input" autoFocus /> ) : ( <span className="detail-value" style={{fontSize: '1.2rem', fontWeight: 'bold'}} onClick={() => handleDoubleClick('name')}>{community.name}</span> )}</div>
                            <div className="detail-item" style={{marginTop: '0.75rem'}}>
                                <span className="detail-label">STATUS</span>
                                <div className={`detail-value ${canEdit ? 'interactive-status-wrapper' : ''}`}>
                                    <span 
                                        className={`status-badge status-${community.status.toLowerCase()} ${canEdit ? 'clickable' : ''}`} 
                                        {...statusLongPress}
                                        onClick={handleStatusClick}
                                        title={canEdit ? "Tap or long-press to change status" : ""}
                                    >
                                        {community.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div className="detail-item"><span className="detail-label">ADDRESS</span>{editingField === 'street' ? ( <input type="text" name="street" value={editedData.address?.street || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('street')} className="form-input" autoFocus /> ) : ( <span className="detail-value" onClick={() => handleDoubleClick('street')}>{community.address?.street || 'N/A'}</span> )}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.5fr 0.5fr', gap: '0.5rem' }}>
                                <div className="detail-item"><span className="detail-label">CITY</span>{editingField === 'city' ? ( <input type="text" name="city" value={editedData.address?.city || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('city')} className="form-input" /> ) : ( <span className="detail-value" onClick={() => handleDoubleClick('city')}>{community.address?.city || 'N/A'}</span> )}</div>
                                <div className="detail-item"><span className="detail-label">STATE</span>{editingField === 'state' ? ( <input type="text" name="state" value={editedData.address?.state || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('state')} className="form-input" /> ) : ( <span className="detail-value" onClick={() => handleDoubleClick('state')}>{community.address?.state || 'N/A'}</span> )}</div>
                                <div className="detail-item"><span className="detail-label">ZIP</span>{editingField === 'zip' ? ( <input type="text" name="zip" value={editedData.address?.zip || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('zip')} className="form-input" /> ) : ( <span className="detail-value" onClick={() => handleDoubleClick('zip')}>{community.address?.zip || 'N/A'}</span> )}</div>
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div className="details-card">
                    <div className="details-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem 2rem', padding: '1.5rem' }}>
                        <div className="detail-item"><span className="detail-label">NAME</span>{editingField === 'name' ? ( <input type="text" name="name" value={editedData.name} onChange={handleInfoChange} onBlur={() => handleSaveInline('name')} onKeyDown={(e) => handleKeyDown(e, 'name')} className="form-input" autoFocus /> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('name')}>{community.name}</span> )}</div>
                        <div className="detail-item">
                            <span className="detail-label">STATUS</span>
                            <div className={`detail-value ${canEdit ? 'interactive-status-wrapper' : ''}`}>
                                <span 
                                    className={`status-badge status-${community.status.toLowerCase()} ${canEdit ? 'clickable' : ''}`} 
                                    {...statusLongPress}
                                    onClick={handleStatusClick}
                                    title={canEdit ? "Tap or long-press to change status" : ""}
                                >
                                    {community.status}
                                </span>
                            </div>
                        </div>
                        <div className="detail-item full-width"><span className="detail-label">STREET</span>{editingField === 'street' ? ( <input type="text" name="street" value={editedData.address?.street || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('street')} onKeyDown={(e) => handleKeyDown(e, 'street')} className="form-input" autoFocus/> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('street')}>{community.address?.street || 'N/A'}</span> )}</div>
                        <div className="detail-item"><span className="detail-label">CITY</span>{editingField === 'city' ? ( <input type="text" name="city" value={editedData.address?.city || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('city')} onKeyDown={(e) => handleKeyDown(e, 'city')} className="form-input" autoFocus/> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('city')}>{community.address?.city || 'N/A'}</span> )}</div>
                        <div className="detail-item"><span className="detail-label">STATE</span>{editingField === 'state' ? ( <input type="text" name="state" value={editedData.address?.state || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('state')} onKeyDown={(e) => handleKeyDown(e, 'state')} className="form-input" autoFocus/> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('state')}>{community.address?.state || 'N/A'}</span> )}</div>
                        <div className="detail-item"><span className="detail-label">ZIP</span>{editingField === 'zip' ? ( <input type="text" name="zip" value={editedData.address?.zip || ''} onChange={handleInfoChange} onBlur={() => handleSaveInline('zip')} onKeyDown={(e) => handleKeyDown(e, 'zip')} className="form-input" autoFocus/> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('zip')}>{community.address?.zip || 'N/A'}</span> )}</div>
                    </div>
                </div>
            );
        }
     
        if (activeTab === 'Contacts') {
            return ( 
                <div className={isMobile ? "mobile-contacts-list" : "contacts-list-container card"}> 
                    <div className="card-body" style={{ padding: 0 }}>
                        <div className="list-header" style={{ padding: '1rem' }}><h4>All Contacts for {community.name}</h4><button className="btn btn-primary" onClick={() => setAddContactModalOpen(true)}><FiPlus className="btn-icon"/> Add</button></div> 
                        {communityContacts.length > 0 ? ( 
                            communityContacts.map((contact, index) => {
                                const creatorId = contact.createdBy?._id || contact.createdBy;
                                const isOwner = creatorId === user.id;
                                const isAdmin = user.roles.includes('Admin') || user.roles.includes('Giga Admin');
                                const canModify = isOwner || isAdmin;
                                return isMobile ? ( <MobileContactCard key={contact._id} contact={contact} onCopy={handleCopy} onEdit={handleOpenEditContactModal} onDelete={onDeleteContact} canModify={canModify} index={index}/> ) : ( <ContactListItem key={contact._id} contact={contact} onCopy={handleCopy} onEdit={handleOpenEditContactModal} onDelete={onDeleteContact} /> );
                            }) 
                        ) : ( <div className="no-tasks-message"><p>No contacts have been added for this community yet.</p></div> )} 
                    </div>
                </div> 
            );
        }
 
        if (activeTab === 'Credentials') {
             return ( 
                <div className={isMobile ? "mobile-credentials-list" : "credential-list-container card"}>
                    <div className="card-body" style={{ padding: 0 }}>
                        <div className="list-header" style={{ padding: '1rem' }}><h4>Credentials</h4><button className="btn btn-primary" onClick={handleOpenAddCredentialModal}><FiKey className="btn-icon"/> Add</button></div>
                        {communityCredentials.length > 0 ? ( 
                            communityCredentials.map((cred, index) => {
                                const creatorId = cred.createdBy?._id || cred.createdBy;
                                const isOwner = creatorId === user.id;
                                const isAdmin = user.roles.includes('Admin') || user.roles.includes('Giga Admin');
                                const canModify = isOwner || isAdmin;
                                return isMobile ? ( <MobileCredentialCard key={cred._id} credential={cred} onCopy={handleCopy} onEdit={handleOpenEditCredentialModal} onDelete={onDeleteCredential} canModify={canModify} index={index}/> ) : ( <CredentialListItem key={cred._id} credential={cred} onCopy={handleCopy} onEdit={handleOpenEditCredentialModal} onDelete={onDeleteCredential} /> );
                            }) 
                        ) : ( <div className="no-tasks-message"><p>No credentials have been saved for this community yet.</p></div> )}
                    </div>
                </div> 
            );
        }
        
        if (activeTab === 'Documentation') {
            return (
                <div className="documentation-container">
                    <div className="card">
                        <div className="card-header"><h3 className="card-title">Documents</h3></div>
                        <div className="card-body">
                            <div className="form-group">
                                <label className="form-label">Upload a New Document</label>
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <CustomFileUpload ref={fileUploadRef} label={isUploading ? "Uploading..." : "Choose Document"} onChange={handleFileSelect} variant="secondary" accept="*/*" />
                                    {selectedFile && ( <button className="btn btn-primary" onClick={handleFileUpload} disabled={isUploading}><FiUploadCloud className="btn-icon" />{isUploading ? 'Uploading...' : 'Start Upload'}</button> )}
                                </div>
                            </div>
                            <div className="items-list" style={{ marginTop: '1.5rem' }}>
                                {communityDocuments.length > 0 ? (
                                    communityDocuments.map((doc, index) => {
                                        const isPy = isPythonFile(doc.originalName);
                                        const isImg = isViewableImage(doc.fileType);
                                        const isVid = isViewableVideo(doc.fileType);
                                        const isTxt = isViewableTextFile(doc.fileType, doc.originalName);
                                        const content = pythonFileContents[doc._id] || '';
                                        const sourceTicket = doc.ownerType === 'Ticket' ? tickets.find(t => t._id === doc.ownerId) : null;
                                        if (isPy) { return ( <Accordion key={doc._id} title={doc.originalName} onToggle={() => handleAccordionToggle(doc)}><pre><code>{content}</code></pre><div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}><button onClick={() => handleDownload(doc)} className="btn btn-secondary btn-sm"><FiDownload className="btn-icon" />Download</button><button onClick={() => handleDeleteDocument(doc._id)} className="btn btn-danger btn-sm"><FiTrash2 className="btn-icon" />Delete</button></div></Accordion> ) }
                                        if (isMobile) { return ( <MobileDocumentCard key={doc._id} doc={doc} onView={handleView} onDownload={handleDownload} onDelete={handleDeleteDocument} sourceTicket={sourceTicket} isImg={isImg} isVid={isVid} index={index}/> ); }
                                        let IconComponent = FiFileText;
                                        if (isImg) IconComponent = FiImage;
                                        else if (isVid) IconComponent = FiFilm;
                                        return ( <div key={doc._id} className="list-item-row" style={{ padding: '1rem 0.5rem', borderBottom: '1px solid var(--gray-100)' }}><div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexGrow: 1 }}>{(isImg || isVid) ? ( isImg ? ( <img src={doc.filePath} alt={doc.originalName} className="doc-list-thumbnail" onClick={() => handleView(doc)} style={{ cursor: 'pointer' }}/> ) : ( <video src={doc.filePath} className="doc-list-thumbnail" muted preload="metadata" onClick={() => handleView(doc)} style={{ cursor: 'pointer' }}/> ) ) : ( <IconComponent size={24} style={{ color: 'var(--gray-500)' }} /> )}<div className="item-info"><span className="item-title" style={{ fontWeight: 600 }}>{doc.originalName}</span><span className="item-meta">{formatFileSize(doc.fileSize)} • Uploaded on {new Date(doc.createdAt).toLocaleDateString()}{doc.uploadedBy && ` by ${doc.uploadedBy.name}`}</span>{sourceTicket && ( <span className="item-meta" style={{ color: 'var(--blue-700)', fontWeight: 500, marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><FiArrowLeft size={12} style={{ transform: 'rotate(180deg)' }}/> From Ticket: {sourceTicket.subject} (#{sourceTicket._id.substring(0,6)})</span> )}</div></div><div className="item-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>{sourceTicket && ( <button onClick={() => navigate(`/tickets/${sourceTicket._id}`)} className="btn btn-secondary btn-sm" title="Go to Ticket"><FiExternalLink className="btn-icon"/> View Ticket</button> )}{(isImg || isTxt || isVid) && ( <button onClick={() => handleView(doc)} className="btn btn-secondary btn-sm" title="View Document"><FiEye className="btn-icon"/> View</button> )}<button onClick={() => handleDownload(doc)} className="btn btn-secondary btn-sm" title="Download Document"><FiDownload className="btn-icon"/> Download</button><button onClick={() => handleDeleteDocument(doc._id)} className="btn btn-danger btn-sm" title="Delete Document"><FiTrash2 className="btn-icon"/> Delete</button></div></div> ); 
                                    })
                                ) : ( <div className="no-tasks-message"><p>No documents have been uploaded for this community yet.</p></div> )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (activeTab === 'Tickets' || activeTab === 'All') {
            const parentTickets = tickets.filter(t => !t.parentId).sort((a, b) => {
                if (a.status === 'Closed' && b.status !== 'Closed') return 1;
                if (b.status === 'Closed' && a.status !== 'Closed') return -1;
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
            if (parentTickets.length === 0) { return ( <div className="card"><div className="card-body"><div className="no-tasks-message"><p>No tickets found for this community.</p></div></div></div> ); }
            if (!isMobile) { return ( <div className="card"><div className="card-header"><h3 className="card-title">Ticket History</h3></div><div className="card-body" style={{ padding: 0 }}><TicketQueue tickets={parentTickets} allTickets={tickets} appointments={[]} onTicketClick={(id) => navigate(`/tickets/${id}`)} emptyMessage="No tickets found." hideClient={true} /></div></div> ); }
            return (
                <div className="mobile-ticket-list">
                    {parentTickets.map((ticket, index) => {
                        const children = tickets.filter(t => { const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId; return pId === ticket._id; }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                        const hasChildren = children.length > 0;
                        const isExpanded = expandedTickets.has(ticket._id);
                        return ( <React.Fragment key={ticket._id}><div className={`mobile-ticket-card ${ticket.status === 'Closed' ? 'closed' : ''}`} onClick={() => navigate(`/tickets/${ticket._id}`)} style={{ animation: 'slideFadeInWithGlow 0.4s ease-out backwards', animationDelay: `${index * 0.05}s` }}><div className="mobile-ticket-card-header"><div className="mobile-ticket-subject-wrapper">{hasChildren && ( <button className="mobile-ticket-toggle" onClick={(e) => toggleTicketExpansion(e, ticket._id)}>{isExpanded ? <FiChevronDown /> : <FiChevronRight />}</button> )}<span className="mobile-ticket-client">{ticket.status}</span></div><span className={`tag priority-${(ticket.priority || 'low').toLowerCase()}`}>{ticket.priority}</span></div><p className="mobile-ticket-subject">{ticket.subject}</p><div className="mobile-ticket-card-footer"><span className="mobile-ticket-techs">{new Date(ticket.createdAt).toLocaleDateString()}</span></div></div>{isExpanded && children.map(child => ( <div key={child._id} className="mobile-ticket-card sub-ticket" onClick={() => navigate(`/tickets/${child._id}`)} style={{marginLeft: '1.5rem', width: 'calc(100% - 1.5rem)'}}><div className="mobile-ticket-card-header"><span className="sub-ticket-indicator-mobile">└</span><span className="mobile-ticket-client">{child.status}</span><span className={`tag priority-${(child.priority||'low').toLowerCase()}`}>{child.priority}</span></div><p className="mobile-ticket-subject">{child.subject}</p></div> ))}</React.Fragment> );
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="community-tabs-wrapper" style={{ width: '100%' }}>
            {/* ✅ RADIAL MENU RENDERED IN TABS CONTEXT */}
            {isStatusMenuOpen && (
                <RadialStatusMenu
                    options={communityStatusOptions}
                    onSelect={handleStatusSelect}
                    onClose={() => setIsStatusMenuOpen(false)}
                    clickPosition={statusMenuPosition}
                />
            )}

            <div className="tabs-nav-scroll-container" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-200)' }}>{TABS.map((tab) => ( <button key={tab} onClick={() => setActiveTab(tab)} className={`btn btn-sm ${activeTab === tab ? 'btn-primary' : 'btn-secondary'}`} style={{ whiteSpace: 'nowrap' }}>{tab}</button> ))}</div>
            <div className="tab-content-area">{renderContent()}</div>
            <ClipboardNotification message={notification.message} isVisible={notification.isVisible} />
            <AddContactModal isOpen={isAddContactModalOpen} onClose={() => setAddContactModalOpen(false)} onAddContact={onAddContact} communityId={community._id} />
            <EditContactModal isOpen={isEditContactModalOpen} onClose={handleCloseEditContactModal} onUpdateContact={onUpdateContact} contact={contactToEdit} />
            <AddEditCredentialModal isOpen={isCredentialModalOpen} onClose={handleCloseCredentialModal} onSubmit={handleCredentialSubmit} communityId={community._id} credentialToEdit={credentialToEdit} isReadOnly={isCredentialReadOnly} />
            <DocumentViewerModal isOpen={isViewerOpen} onClose={() => setIsViewerOpen(false)} document={viewingDocument} content={viewerContent} />
            <ImageViewerModal isOpen={isImageViewerOpen} onClose={() => setIsImageViewerOpen(false)} imageUrl={viewerContent} docName={viewingDocument?.originalName} />
            <VideoViewerModal isOpen={isVideoViewerOpen} onClose={() => setIsVideoViewerOpen(false)} videoUrl={viewerContent} docName={viewingDocument?.originalName} />
        </div>
    );
};

// =================================================================================
// MAIN COMPONENT DEFINITION
// =================================================================================
const CommunityDetails = ({ communities, contacts, onAddContact, onUpdateContact, onDeleteContact, onUpdateCommunity, credentials, onAddCredential, onUpdateCredential, onDeleteCredential, documents, onAddDocument, onDeleteDocument, apiCallForBlob }) => {
    const { communityId } = useParams();
    const navigate = useNavigate();
    const callApi = useApi();
    const isMobile = useIsMobile();
    
    const community = communities.find(c => c._id === communityId);

    const [communityTickets, setCommunityTickets] = useState([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(true);
    const [mapState, setMapState] = useState({
        status: 'idle',
        iframeSrc: '',
        errorMessage: ''
    });

    const [showMapOptions, setShowMapOptions] = useState(false);

    // --- Ticket Fetching ---
    useEffect(() => {
        if (community && community.name) {
            const fetchTicketsForCommunity = async () => {
                setIsLoadingTickets(true);
                const result = await callApi(`tickets?client=${encodeURIComponent(community.name)}&limit=500`);
                if (result.success && Array.isArray(result.data.data)) {
                    setCommunityTickets(result.data.data);
                } else {
                    console.error("Failed to fetch tickets for community:", result.error);
                    setCommunityTickets([]);
                }
                setIsLoadingTickets(false);
            };
            fetchTicketsForCommunity();
        }
    }, [community, callApi]);

    // --- Map Logic ---
    useEffect(() => {
        const hasValidAddress = community?.address?.street?.trim();

        if (!hasValidAddress) {
            setMapState({ status: 'idle', iframeSrc: '', errorMessage: '' });
            return;
        }

        const cacheKey = `map-url-${community._id}`;
        const cachedUrl = sessionStorage.getItem(cacheKey);

        if (cachedUrl) {
            setMapState({ status: 'success', iframeSrc: cachedUrl, errorMessage: '' });
            return;
        }

        const fetchMapUrl = async () => {
            setMapState(prev => ({ ...prev, status: 'loading' }));
            const fullAddress = `${community.address.street}, ${community.address.city || ''}, ${community.address.state || ''} ${community.address.zip || ''}`;
            try {
                const response = await fetch(`/api/maps/geocode?address=${encodeURIComponent(fullAddress)}`);
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Address could not be located on the map.');
                }
                const data = await response.json();
                sessionStorage.setItem(cacheKey, data.iframeSrc);
                setMapState({ status: 'success', iframeSrc: data.iframeSrc, errorMessage: '' });
            } catch (err) {
                setMapState({ status: 'error', iframeSrc: '', errorMessage: err.message });
            }
        };
        fetchMapUrl();
    }, [community]);

    const handleBack = () => {
        navigate(-1);
    };

    if (!community) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="header-left-details">
                        <button onClick={handleBack} className="back-btn" title="Go Back">
                            <FiArrowLeft />
                        </button>
                        <h2 className="page-title">Community Not Found</h2>
                    </div>
                </div>
                <div className="card">
                    <p>The community you are looking for does not exist.</p>
                </div>
            </div>
        );
    }
    
    const fullAddress = (community.address && community.address.street)
        ? `${community.address.street}, ${community.address.city || ''}, ${community.address.state || ''} ${community.address.zip || ''}`.replace(/ ,/g, '').trim()
        : 'No address provided.';
    const encodedAddress = encodeURIComponent(fullAddress);
    const googleMapsUrl = `http://googleusercontent.com/maps.google.com/?q=${encodedAddress}`;
    const appleMapsUrl = `http://maps.apple.com/?q=${encodedAddress}`;

    const showLocationCard = community.address && community.address.street;

    const locationMap = showLocationCard && (
        <div className="card location-card">
             <div className="card-header">
                <h3 className="card-title">Location</h3>
            </div>
            <div className="card-body">
                <div className="location-card-body">
                    <p className="location-address">{fullAddress}</p>
                </div>
                
                <div style={{ display: 'flex', marginBottom: '1rem', height: '32px' }}>
                    {!showMapOptions ? (
                        <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowMapOptions(true)}
                            style={{ 
                                display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'center',
                                animation: 'fadeIn 0.2s ease-out'
                            }}
                        >
                            <FiMap size={14} /> Open in Maps
                        </button>
                    ) : (
                        <div style={{ 
                            display: 'flex', gap: '0.5rem', width: '100%',
                            animation: 'scaleIn 0.2s ease-out forwards'
                        }}>
                            <a 
                                href={appleMapsUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="btn btn-secondary btn-sm"
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', flex: 1, justifyContent: 'center' }}
                                title="Open in Apple Maps"
                            >
                                <FaApple size={14} /> Apple
                            </a>
                            <a 
                                href={googleMapsUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="btn btn-secondary btn-sm"
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', flex: 1, justifyContent: 'center' }}
                                title="Open in Google Maps"
                            >
                                <FaGoogle size={14} /> Google
                            </a>
                        </div>
                    )}
                </div>

                <MapView 
                    status={mapState.status}
                    iframeSrc={mapState.iframeSrc}
                    errorMessage={mapState.errorMessage}
                    addressStreet={community.address.street}
                />
            </div>
        </div>
    );

    const mainContent = isLoadingTickets ? (
        <div className="loading-indicator">Loading tickets...</div>
    ) : (
        <CommunityContentTabs
            isMobile={isMobile}
            community={community}
            tickets={communityTickets}
            contacts={contacts}
            onAddContact={onAddContact}
            onUpdateContact={onUpdateContact}
            onDeleteContact={onDeleteContact}
            onUpdateCommunity={onUpdateCommunity}
            credentials={credentials}
            onAddCredential={onAddCredential}
            onUpdateCredential={onUpdateCredential}
            onDeleteCredential={onDeleteCredential}
            documents={documents}
            onAddDocument={onAddDocument}
            onDeleteDocument={onDeleteDocument}
            navigate={navigate}
            apiCallForBlob={apiCallForBlob}
        />
    );

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left-details">
                    <button onClick={handleBack} className="back-btn" title="Go Back">
                        <FiArrowLeft />
                    </button>
                    <div>
                        <h2 className="page-title">{community.name} Details</h2>
                        <span className="page-subtitle">A brief overview of associated tickets, contacts, and more.</span>
                    </div>
                </div>
            </div>
            
            {isMobile ? (
                <div className="details-main-column" style={{gap: '1.5rem'}}>
                    {mainContent}
                    {locationMap && <div className="location-card-container">{locationMap}</div>}
                </div>
            ) : (
                <div className="details-page-grid">
                    <div className="details-main-column">
                        {mainContent}
                    </div>
                
                    {locationMap && (
                        <div className="details-side-column location-card-container">
                            {locationMap}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CommunityDetails;